<script setup lang="ts">
import cloneDeep from 'lodash/cloneDeep'
import { useDialog } from 'primevue/usedialog'
import { DateTime } from 'luxon'
import { useMcdRoutes } from '~/composables/useMcdRoutes'
import {
  RestaurantAreaType,
  OpenServicesState,
  MismatchingStateType,
  MismatchingState,
  RestaurantAreas,
  StoreRestaurantAreas,
} from '~/lib/interfaces/restaurant'
import {
  Category,
  ProductEcommerce,
  OptionsGroupsMain,
  OptionsGroupsChild,
  productInfoKey,
  ExclusiveEventTypes,
} from '~/lib/services/store/catalog/catalog.dtos'
import { CartProduct } from '~/lib/services/store/catalog/cart.dtos'
import { RestaurantInfo } from '~/lib/services/store/restaurant/restaurant.dto'
import { foundProductByIdentifier, isProductEnabledByAreas, productToDTO } from '~/utils/storeUtils'
import { McDiaComponent, McDiaMessageStatus } from '~/lib/models/mcdia'
import { ToastType } from '~/lib/models/ui/toast'
import { getMcDiaMessageComponent } from '~/utils/datesUtils'
import { StoreOrder } from '~/lib/services/store/order/order.dto'
import {
  OptionsGroupsType,
  ProductPromotion,
  ProductSizeType,
} from '~/lib/services/store/common.dto'
import { promosService } from '~/lib/services/promos/promos.service'
import { CHECK_PROMO_ERROR, SECTION_NAME_PROMOTION } from '~/lib/models/promotions'
import { StoreOrderStatus } from '~/lib/models/order'
import { isTaxCatalogActiveKey } from '~/lib/interfaces/symbols'
import { ProductErrorType } from '~/lib/models/product-limit'

const props = defineProps<{
  product: ProductEcommerce
  category: Category // WIP
  restaurantInfo: RestaurantInfo
  area: RestaurantAreaType
  notMatchingStatus?: MismatchingStateType
  editing?: boolean
  editingIndex?: number
  freeShippingPromo?: ProductPromotion
}>()

const emit = defineEmits(['on-change-size', 'save-edition'])

const isMobile = inject<Ref<boolean>>('isMobile')
const appStore = useAppStore()
const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()
const { menuProductsPath } = useRestaurantActions()
const dialog = useDialog()
const { format } = useFormatAmount()
const { t } = useLocale()
const { cartRoute, getCategoryRoute, navigateToHome, getProductRoute, myOrdersRoute } =
  useMcdRoutes()
const { trackAddToCart, trackIncompatibleProduct } = useGTM()
const { $toast } = useNuxtApp()
const { orderServiceRef } = useStoreService()
const orderService = orderServiceRef()
const { handleStoreError } = useStoreResponse()
// Catalogs
const { catalogServiceRef } = useStoreService()
const catalogService = catalogServiceRef()
const { getCheckPromoIds, getCheckPromosInput } = usePromotions()
const { userSegmentation } = useProductPromos()
const { showErrorPromotion, errorCumulativePromotion, isComulativePromotionError } =
  usePromotionErrorHandling()
const validPromo = ref<ProductPromotion | false>()

// Provide product identifier, combo, name
provide(productInfoKey, {
  identifier: props.product.identifier,
  name: props.product.name,
  combo: props.product.combo,
})

provide(
  isTaxCatalogActiveKey,
  computed(() => appStore.isTaxCatalogActive)
)

const unit = ref(1)
const unitLimitProduct = ref<number | null>(null)
const customizing = ref(false)
// available optionGroup inmmutable
const availableOptionGroups = getAvailableOptionsGroups(props.product.optionsGroups)
// Stores current user personalization
const currentOptionsGroups = ref<OptionsGroupsMain[]>(availableOptionGroups)
const restaurantSelectedOpenState = ref<OpenServicesState>()
// Message response service limit event
const messageLimitProduct = ref('')
const isUnavailable = ref(false)
const productPlu = computed(() => props.product.identifier ?? null)

const validProductPromo = computed(() => {
  return validPromoForBubble(props.product.promotions, userSegmentation.value) || null
})
const hasFreeShippingPromo = computed(() =>
  validFreeShippingPromo(props.product.promotions, userSegmentation.value)
)

const isDeepLinkProduct = computed(() => appStore.isDeeplinkPromotionProduct)

const productName = computed(() => {
  if (appStore.isHideCombosActive) {
    return props.product.genericName ?? props.product.name
  }
  return props.product.name
})
/**
 * Applies a promotional discount based on the quantity of items selected.
 * The discount is calculated using the per-unit discount provided by the promo
 * (i.e., the difference between price and priceWithDiscount),
 * and then multiplied by the total quantity.
 *
 * @param base The total amount (base + extras)
 * @param quantity The number of units selected.
 * @returns The final amount after applying the total discount.
 */
const applyPromoDiscount = (base: number, quantity: number) => {
  if (cartEditingProduct.value?.isPromo && props.editing) return base
  if (!validPromo.value || validPromo.value.prices === null) return base
  const prices = validPromo.value.prices
  // Ensure the price and priceWithDiscount values are valid
  if (!prices || prices.price === null || prices.priceWithDiscount === null) return base
  // Calculate total discount from per-unit discount and quantity
  const discountBeforeExtras = prices.price - prices.priceWithDiscount
  const totalDiscount = discountBeforeExtras * quantity
  return base - totalDiscount
}

const computedAmount = computed(() => {
  /* WIP - until back takes price.amount = 0 for magically included optionsGroups */
  const computedOptionsGroups = currentOptionsGroups.value
  const productCustomizing = { ...props.product, optionsGroups: computedOptionsGroups }
  let totalAmountUnits = getProductTotalUnitsAmount(productCustomizing, unit.value)
  totalAmountUnits = applyPromoDiscount(totalAmountUnits, unit.value)
  return totalAmountUnits
})
const computedPrice = computed(() => {
  const amount = computedAmount.value
  return format(amount || 0)
})

const filteredProductSizes = computed(
  () => props.product.sizes?.filter(s => s.id !== ProductSizeType.NONE && s.active) || []
)

const showFilteredProductSizes = computed(() =>
  filteredProductSizes.value.some(size => size.code === props.product.identifier)
)

const availableAreasOnSchedule = computed(() => {
  const restaurant = restaurantStore.restaurant
  if (!restaurant || !restaurant.active || !restaurantSelectedOpenState.value) return false
  return isProductEnabledByAreas(props.product.areas, restaurantSelectedOpenState.value)
})

const isMcDiaExpiredProduct = computed(() => {
  if (!props.product.mcDia) return false
  const { dateFrom, dateTo } = props.product.mcDia
  const status = getMcDiaMessageStatus({
    dateFrom,
    dateTo,
  })
  return status === McDiaMessageStatus.AFTER_DATE
})

// TODO:
// other options (mcdia, etc...)
const canAddToCart = computed(() => {
  if (disabledUserInteraction.value) return true
  if (!props.product.active) return false
  // check McDia product expired
  if (isMcDiaExpiredProduct.value) return false
  // schedule restaurant check (when no editing and no allowOrderRestaurantClosed)
  if (
    !props.editing &&
    !appStore.ecommerceConfig?.allowOrderRestaurantClosed &&
    !availableAreasOnSchedule.value
  )
    return false
  if (!props.product.optionsGroups.length) return true // no personalization
  // is customizing actions are disabled
  if (customizing.value) return false
  /* Check all optionsGroups Mandatory selected */
  const choiceGroups = currentOptionsGroups.value.filter(
    optG => optG.type === OptionsGroupsType.CHOICE
  )
  if (!choiceGroups.length) return true
  // check deep inside all optionsGroups with option has all optionsGroups child selected
  return choiceGroups.every(optG => {
    const optionSelected = optG.options.find(opt => opt.qty === 1)
    if (!optionSelected) return false
    const thirdLevelChoiceGroups = optionSelected.optionsGroups.filter(
      optGChild => optGChild.type === OptionsGroupsType.CHOICE
    )
    if (!thirdLevelChoiceGroups.length) return true
    const areThirdLevelChoicesSelected = thirdLevelChoiceGroups.every(optGChild =>
      optGChild.options.some(optChild => optChild.qty === 1)
    )
    if (!areThirdLevelChoicesSelected) return false
    // check 5º level optGroups
    const fifthLevelChoiceOptionGroups: OptionsGroupsChild[] = thirdLevelChoiceGroups.reduce(
      (acc, optGThirdLevel) => {
        const fifthLevelOptionSelected = optGThirdLevel.options.find(
          optFifthLevel => optFifthLevel.qty === 1
        )
        if (!fifthLevelOptionSelected || !fifthLevelOptionSelected.optionsGroups.length) return acc
        acc.push(
          ...(fifthLevelOptionSelected.optionsGroups.filter(
            optGFifth => optGFifth.type === OptionsGroupsType.CHOICE
          ) as OptionsGroupsChild[])
        )
        return acc
      },
      [] as OptionsGroupsChild[]
    )
    // if are is TRUE && not have 5º level option with optionsGroups => true
    if (!fifthLevelChoiceOptionGroups.length) return true
    return fifthLevelChoiceOptionGroups.every(optGFifth =>
      optGFifth.options.some(optFifthChild => optFifthChild.qty === 1)
    )
  })
})

const disabledUserInteraction = computed(() => {
  if (!props.notMatchingStatus) return false
  return !!props.notMatchingStatus
})

const cartEditingProduct = computed(() => {
  if (!props.editing) return undefined
  return appStore.cart.products[props.editingIndex || 0]
})

const cartProductPriceWithDiscount = computed(() => {
  if (!props.editing) return undefined
  if (!cartEditingProduct.value?.isPromo) return undefined
  // Cart promo has computedPrice.priceWithDiscount, but while editing we can add extras so priceWithDiscount could be greater
  const priceWithDiscount =
    validProductPromo?.value?.prices?.priceWithDiscount ?? computedAmount.value
  return format(priceWithDiscount)
})

const updatePersonalization = (ev: OptionsGroupsMain[]) => {
  currentOptionsGroups.value = ev
}

const isProductLimitEvent = computed(
  () => props.product.exclusivePayEvent?.type === ExclusiveEventTypes.PRODUCT_LIMIT_EVENT
)

const isAddDisabledByLimit = computed(() => {
  if (!isProductLimitEvent || unitLimitProduct.value === null) return false
  return unit.value >= unitLimitProduct.value
})

/* Actions */

const addProductToCart = async ({ onlyAdd = false }) => {
  const canContinue = await canContinueAddingToCart()
  if (!canContinue) return
  await addToCart({ onlyAdd })
  if (validPromo.value) validPromo.value.active = true
}

const continueToCart = async () => {
  const canContinue = await canContinueAddingToCart()
  if (!canContinue) return
  await addToCart({ onlyAdd: false })
  if (validPromo.value) validPromo.value.active = true
  await navigateTo(cartRoute())
}

const checkValidPromotion = async (): Promise<ProductPromotion | undefined> => {
  const productPromo = validPromoForBubble(props.product.promotions, userSegmentation.value)
  if (!productPromo) return undefined

  const checkPromosInput = getCheckPromosInput({
    promotionsId: [productPromo.id],
    products: [props.product.id],
  })
  if (!checkPromosInput) return undefined
  // Check if limited promotion
  const checkPromosResponse = await promosService.checkPromos(checkPromosInput)
  if (!checkPromosResponse[0]?.valid) return undefined
  return productPromo
}

const checkValidPromotionForCart = async (): Promise<boolean> => {
  if (!validPromo.value) return true
  const checkPromosInput = getCheckPromoIds({
    productId: props.product.id,
    promoId: validPromo.value.id,
  })
  if (!checkPromosInput) return false
  // Check if limited promotion
  const checkPromosResponse = await promosService.checkPromos(checkPromosInput)
  const limitedPromotion = checkPromosResponse.find(
    promo =>
      promo.status === CHECK_PROMO_ERROR.CumulativePromotionsNotAllowed ||
      promo.status === CHECK_PROMO_ERROR.AutoApplicablePromotionNotAllowed
  )
  if (limitedPromotion) {
    const decision = await showDialogAlert({
      dialog,
      data: {
        title: t('errors.alerts.limitedPromoProduct.title'),
        body: t('errors.alerts.limitedPromoProduct.body'),
        btnPrimary: t('errors.alerts.limitedPromoProduct.accept'),
        btnSecondary: t('errors.alerts.limitedPromoProduct.cancel'),
        headerImage: '/images/icons/mcd-bag.svg',
        onCloseUndefined: true,
        verticalActions: true,
      },
    })
    // user close modal, don't add product to cart
    if (decision === undefined) return false
    if (decision) {
      // Not apply actual promo
      if (validPromo.value) validPromo.value.active = false
    } else {
      // apply new and disable actual cart promotions
      appStore.disableAllProductPromotions()
    }
  }
  // Check if employee discount is applied
  const employeeDiscount = appStore.appliedPromotions?.find(
    promo => promo.code === undefined && promo.enabled
  )
  if (employeeDiscount) {
    const decision = await showDialogAlert({
      dialog,
      data: {
        title: t('errors.alerts.employeeDiscount.title'),
        body: t('errors.alerts.employeeDiscount.body'),
        btnPrimary: t('errors.alerts.employeeDiscount.accept'),
        btnSecondary: t('errors.alerts.employeeDiscount.cancel'),
        headerImage: '/images/icons/mcd-bag.svg',
        onCloseUndefined: true,
        verticalActions: true,
      },
    })
    if (decision || decision === undefined) {
      // Not apply actual promo
      if (validPromo.value) validPromo.value.active = false
    } else {
      // remove employee discount
      appStore.removePromoById(employeeDiscount.promotionId)
    }
  }
  return true
}

const canContinueAddingToCart = async (): Promise<boolean> => {
  const authStore = useAuthStore()
  // Disabled user interaction - is another restaurant or area visited
  if (disabledUserInteraction.value) {
    await onMismatchingRestaurantOrArea()
    return false
  }

  const validPromo = await checkValidPromotionForCart()
  if (!validPromo) return false

  // Limit order in progress
  try {
    if (!appStore.cart.products.length && authStore.token) {
      const pendings: StoreOrder[] = await orderService.getPendingOrdersByUser({
        customerToken: authStore.token,
        countryCode: appStore.countryCode,
      })
      const maxOrdersLimit = restaurantStore.restaurant?.limits.limitOrder || 0
      const pendingOrdersFiltered = pendings.filter(order => {
        if (order.status === StoreOrderStatus.FLEX_STAGING) {
          if (order.type === RestaurantAreas.MOP && order.area === StoreRestaurantAreas.AUT)
            return false
          else return true
        } else if (order.status === StoreOrderStatus.FLEX_DELIVERED) return false
        else return true
      })
      if (maxOrdersLimit && pendingOrdersFiltered.length >= maxOrdersLimit) {
        const multiple = maxOrdersLimit > 1
        const alertKey = `errors.alerts.orderInProgress${multiple ? 'Multiple' : ''}`
        // must show orderLimit confirmation
        const confirmation = await showDialogAlert({
          dialog,
          data: {
            title: t(`${alertKey}.title`),
            body: t(`${alertKey}.body`),
            btnPrimary: t(`${alertKey}.accept`),
            btnSecondary: t(`${alertKey}.cancel`),
          },
        })

        if (confirmation) {
          // go to MyOrders
          await navigateTo(myOrdersRoute())
        }
        return false
      }
    }
  } catch (error) {
    return false
  }
  // Mixing mcDia products
  if (!checkMcDiaValidation()) return false
  return true
}

const addToCart = async ({ onlyAdd = false }) => {
  const cartProduct = getCartProduct()
  const catalogProduct = { ...props.product }
  if (unitLimitProduct.value && unitLimitProduct.value > 0)
    catalogProduct.limitProduct = unitLimitProduct.value
  trackAddToCart([cartProduct], undefined)
  // Accion para agregar un producto y mostrar notificacion
  appStore.setProductAddedAction(true)
  //
  await appStore.addCartProduct(cartProduct, catalogProduct)
  if (isMobile?.value && onlyAdd) {
    // is mobile and only is adding to cart
    if (appStore.productTypeOnDetail) await navigateTo(cartRoute())
    else await navigateToHome()
  }
  if (props.product.mcDia) {
    const mcdiaDates = {
      dateFrom: props.product.mcDia.dateFrom,
      dateTo: props.product.mcDia.dateTo,
    }
    const advanceSalesMessages = appStore.ecommerceConfig?.messagesAdvanceSalesDates
    if (!advanceSalesMessages) return false
    const area = restaurantStore.areaSelected!.toLowerCase() as 'mop' | 'dlv'
    const message = getMcDiaMessageComponent({
      component: McDiaComponent.CART,
      messages: advanceSalesMessages[area],
      mcdiaDates,
    })
    appStore.setMcDiaCartToastMessage(message)
  }
}

const saveEditionToCart = () => {
  const cartProduct = getCartProduct()
  emit('save-edition', cartProduct)
}

const getPromotionsForCart = () => {
  const validPromotions: ProductPromotion[] = []
  if (validPromo.value) validPromotions.push(validPromo.value)
  if (hasFreeShippingPromo.value) validPromotions.push(props.product.promotions[0])
  return validPromotions
}

const getCartProduct = () => {
  const product = cloneDeep(props.product)

  product.optionsGroups = currentOptionsGroups.value

  const productDTO = productToDTO(product, unit.value)
  const computedPrice = {
    ...(cartEditingProduct.value?.computedPrice || {}),
    price: computedAmount.value,
    priceUnit: computedAmount.value / unit.value,
  }
  const customization = getCartCustomization(product.optionsGroups)
  const productType = appStore.productTypeOnDetail || productDTO.productType
  const promotions = getPromotionsForCart()
  const cartProduct: CartProduct = {
    ...productDTO,
    name: props.product.name,
    genericName: props.product.genericName,
    image: props.product.imageUrl,
    categoryIdRfm: props.category.idRfm,
    categoryTitle: props.category.title,
    computedPrice,
    customization,
    isMcDia: !!product.mcDia,
    // promos
    isPromo: cartEditingProduct.value?.isPromo,
    isPromoApplied: cartEditingProduct.value?.isPromoApplied,
    promotions,
    // productType
    productType,
    limitReached: cartEditingProduct.value?.limitReached,
  }

  return cartProduct
}

/* Returns false if fails to add product because mcDiaValidation and true if its ok  */
const checkMcDiaValidation = (): boolean => {
  const cartProds = appStore.cart.products || []
  const cartProdsLength = cartProds.length
  if (!cartProdsLength) return true

  // array of mcdia products
  const arrMcDiaProducts = cartProds.filter(prod => prod.isMcDia)
  let hasToast = false
  let hasMcDiaToast = null
  // no mcdia prods and trying to add one mcdia prod show toast hasNoMcdia
  if (!arrMcDiaProducts.length && !!props.product.mcDia) {
    hasToast = true
    hasMcDiaToast = false
  }
  // there are mcdiaProds on cart and trying to add regular one
  else if (arrMcDiaProducts.length && !props.product.mcDia) {
    hasToast = true
    hasMcDiaToast = true
  }

  if (hasToast) {
    const advanceSalesMessages = appStore.ecommerceConfig?.messagesAdvanceSalesDates
    if (!advanceSalesMessages) return false
    const area = restaurantStore.areaSelected!.toLowerCase() as 'mop' | 'dlv'
    const productDetailMessages = advanceSalesMessages[area].productDetail
    const content = hasMcDiaToast
      ? productDetailMessages.warningCartHasMcDiaProducts
      : productDetailMessages.warningCartHasNoMcDiaProducts
    $toast.show({
      type: ToastType.WARNING,
      content,
    })
    trackIncompatibleProduct({ preOrder: !hasMcDiaToast })
    return false
  }
  return true
}

const onMismatchingRestaurantOrArea = async () => {
  const hasRestaurantSelected = restaurantStore.isRestaurantSelected
  const notMatchingKey = props.notMatchingStatus!
  const alertKey = `productDetail.mismatching.alerts.${notMatchingKey}`

  if (!hasRestaurantSelected) {
    const isVisitingAreaMOP = props.area === RestaurantAreas.MOP
    // isMOP
    // primary - Select visiting restaurant
    // secondary - Change restaurant modal
    // !isMOP
    // primary - Change restaurant modal
    const btnPrimary = isVisitingAreaMOP ? t(`${alertKey}.accept`) : t(`${alertKey}.cancel`)
    const btnSecondary = isVisitingAreaMOP ? t(`${alertKey}.cancel`) : undefined
    const result = await showDialogAlert({
      dialog,
      data: {
        title: t(`${alertKey}.title`),
        body: t(`${alertKey}.body`, { restaurantName: props.restaurantInfo.name || '' }),
        btnPrimary,
        btnSecondary,
        onCloseUndefined: true,
      },
    })
    // undefined when close (cancelBtn - false, acceptBtn - true)
    if (result === undefined) return
    if (result) {
      if (isVisitingAreaMOP) {
        try {
          // get detail, set restaurant, and keep static on here
          const { code: restaurantCode, id: restaurantId } = props.restaurantInfo
          const area = props.area
          await restaurantStore.getRestaurantByCode({
            code: restaurantCode,
            area,
          })
          restaurantStore.setRestaurant(restaurantStore.restaurant!)
          await appStore.loadCategories(restaurantId, area)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(
            '🚀 ~ file: ProductViewEcommerce.vue:252 ~ onMismatchingRestaurantOrArea ~ error:',
            error
          )
        }
      } else restaurantStore.openChangeRestaurantModal()
    } else if (isVisitingAreaMOP) restaurantStore.openChangeRestaurantModal()
  } else {
    const isMismatchingArea = notMatchingKey === MismatchingState.MISMATCHING_AREA
    const isMismatchingAreaAndRestaurant =
      notMatchingKey === MismatchingState.MISMATCHING_AREA_RESTAURANT

    const result = await showDialogAlert({
      dialog,
      data: {
        title: t(`${alertKey}.title${isMismatchingArea ? props.area : ''}`),
        body: t(`${alertKey}.body`),
        btnPrimary: t(`${alertKey}.accept`),
        btnSecondary: t(`${alertKey}.cancel`),
        onCloseUndefined: true,
      },
    })
    // undefined when close (cancelBtn - false, acceptBtn - true)
    if (result === undefined) return
    // primary action
    if (result) {
      if (isMismatchingArea) {
        // show modal change restaurant
        restaurantStore.openChangeRestaurantModal()
      } else if (isMismatchingAreaAndRestaurant) {
        // navigate to home
        return await navigateToHome()
      } else {
        // find this prod on my catalog
        const foundedProdOnCatalog = foundProductByIdentifier(
          appStore.categories || [],
          props.product.identifier
        )
        // the product was not found on this restaurant catalog
        if (!foundedProdOnCatalog) {
          const notFoundProductAlertKey =
            'productDetail.mismatching.alerts.notFoundProductOnCatalog'
          const resultNotFoundProduct = await showDialogAlert({
            dialog,
            data: {
              title: t(`${notFoundProductAlertKey}.title`),
              body: t(`${notFoundProductAlertKey}.body`),
              btnPrimary: t(`${notFoundProductAlertKey}.accept`),
            },
          })
          if (resultNotFoundProduct) return await navigateTo(menuProductsPath.value)
        } else {
          // navigate to this product
          const {
            category: { title: catTitle },
            product: { name: prodName },
          } = foundedProdOnCatalog
          const productRoute = getProductRoute({
            restaurantInfo: restaurantStore.selectedRestaurant!.restaurant!,
            categoryTitle: catTitle,
            productName: prodName,
          })
          return await navigateTo(productRoute)
        }
      }
    }
    // secondary action
    else if (
      isMismatchingAreaAndRestaurant ||
      notMatchingKey === MismatchingState.MISMATCHING_RESTAURANT
    ) {
      // show modal change restaurant
      restaurantStore.openChangeRestaurantModal()
    }
  }
}

const computeRestaurantOpenState = () => {
  const restaurant = restaurantStore.restaurant
  if (restaurant) {
    restaurantSelectedOpenState.value = getRestaurantOpenState({
      datetime: DateTime.utc(),
      active: restaurant.active,
      timezone: restaurant.timezone,
      code: restaurant.code,
      timePeriods: restaurant.timeSlotsService,
      pickupTime: appStore.ecommerceConfig?.pickupTime || 0,
    })

    restaurantStore.setRestaurantOpenState(restaurantSelectedOpenState.value)
  }
}

const handleDeepLinkProductDetail = () => {
  const content = `{shoppingCart.promotions.deepLinks.addCartProducts}`
  $toast.show({
    type: ToastType.INFO,
    content,
  })
  appStore.resetDeeplinkPromotionType()
}

const checkProductLimit = async () => {
  try {
    const fetchProductInput = {
      token: authStore.token!,
      countryCode: appStore.countryCode,
      body: {
        plu: productPlu.value,
        countryCode: appStore.countryCode,
      },
    }
    const resp = await catalogService.checkProductLimitAvailability(fetchProductInput)
    //
    if (resp.units) {
      const units = resp.units
      unitLimitProduct.value = units
      messageLimitProduct.value = t('productDetail.productLimit', { count: unitLimitProduct.value })
    }
  } catch (error: any) {
    const { key } = handleStoreError(error)
    if (([ProductErrorType.status861, ProductErrorType.status862] as string[]).includes(key)) {
      messageLimitProduct.value = t(`errors.alerts.${key}.body`)
      isUnavailable.value = true
      unitLimitProduct.value = 1
    }
  }
}

// Watch on size change
watch(
  () => props.product.identifier,
  () => {
    // available optionGroup inmmutable
    const availableNewOptionGroups = getAvailableOptionsGroups(props.product.optionsGroups)
    // Clear current optionsGroups
    currentOptionsGroups.value = availableNewOptionGroups
    computeRestaurantOpenState()
  },
  { immediate: true }
)

// On change restaurant selected MimatchingState.NO_RESTAURANT_SELECTED
watch(
  () => restaurantStore.restaurant?.code,
  () => {
    computeRestaurantOpenState()
  },
  { immediate: true }
)

onMounted(async () => {
  computeRestaurantOpenState()
  validPromo.value = validPromoForBubble(props.product.promotions, userSegmentation.value)
  // Check valid promotion
  const validPromotion = await checkValidPromotion()
  if (validPromotion) validPromo.value = validPromotion
  // !important Take into account units of cart product on enter while editing
  if (cartEditingProduct.value) {
    unit.value = cartEditingProduct.value.unit
  }
  if (!isProductLimitEvent.value) return
  if (!authStore.isUserLogged || !productPlu.value) return
  await checkProductLimit()
})

watch(
  isDeepLinkProduct,
  newValue => {
    if (newValue) handleDeepLinkProductDetail()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  // reset productType state always
  appStore.setProductTypeOnDetail(undefined)
})

// Detect error when adding employee discount or coupon, within the cart with auto-applicable promotions
watch(errorCumulativePromotion, newValue => {
  if (newValue && isComulativePromotionError(newValue)) {
    showErrorPromotion(SECTION_NAME_PROMOTION.PRODUCT_DETAIL)
  }
})
</script>

<template>
  <!-- Navigation backwards to category -->
  <header class="sticky top-0 z-10 w-full bg-white md:hidden">
    <NuxtLink
      :to="
        getCategoryRoute({
          restaurantInfo: restaurantInfo,
          categoryTitle: category.title,
          routeArea: area,
        })
      "
      class="flex w-full flex-row items-center"
    >
      <img src="/images/icons/arrowUp.svg" class="m-4 -rotate-90 cursor-pointer" alt="back" />
      <p class="font-bold">{{ category.title }}</p>
    </NuxtLink>
  </header>
  <div
    class="mx-auto flex flex-col gap-5 p-5 pb-[9rem] md:grid md:w-[100%] md:grid-cols-[40%_1fr_55%] md:grid-rows-1 md:justify-center md:gap-0 lg:w-[75%]"
  >
    <section class="w-full md:col-span-1">
      <div class="flex w-full flex-col md:sticky md:top-[9rem]">
        <!-- Product Image -->
        <ImageResolver
          class="flex h-auto w-9/12 self-center"
          :url="product.imageUrl"
          :alt="productName"
        />
        <div class="md:px-4 lg:px-10">
          <!-- Product actions: Add qty, price, buttons-->
          <ClientOnly>
            <ProductActions
              v-model:unit="unit"
              :price="computedPrice"
              :price-with-discount="cartProductPriceWithDiscount"
              :can-add="canAddToCart"
              :disable-add="
                (cartEditingProduct?.isPromo && cartEditingProduct.limitReached) ||
                disabledUserInteraction ||
                isAddDisabledByLimit
              "
              :disable-substract="cartEditingProduct?.isPromo || disabledUserInteraction"
              :editing="editing || false"
              :is-unavailable="isUnavailable"
              @add="addProductToCart({ onlyAdd: true })"
              @continue="continueToCart"
              @save="saveEditionToCart"
            />
          </ClientOnly>
          <!-- Info banners DESKTOP: Cannot be pickup on area, McDia info ... etc -->
          <ProductAlerts
            :product="product"
            :not-matching-status="notMatchingStatus"
            :area="area"
            class="hidden md:block"
          />
        </div>
        <PayEventInfo
          v-if="!isMobile && props.product.exclusivePayEvent"
          :data="props.product.exclusivePayEvent"
          :message="messageLimitProduct"
        />
      </div>
    </section>
    <section data-test="data-product" class="flex w-full flex-col md:col-start-3 md:col-end-3">
      <!-- Name -->
      <h1 data-test="product-name" class="my-4 text-start text-2xl font-bold md:mt-10 md:text-5xl">
        {{ productName }}
      </h1>

      <div class="flex flex-col gap-2 md:gap-4">
        <!-- FreeShippingBubble -->
        <FreeShippingBubble v-if="hasFreeShippingPromo" big />
        <!-- HasPromo -->
        <PromoBubble big :promotions="props.product.promotions" />
        <!-- Inmutable price -->
        <ProductPrice :product="props.product" />
      </div>

      <!-- description -->
      <p class="my-4 text-sm text-mcd-secondaryDarkGrey md:text-base">{{ product.description }}</p>

      <!-- Info banners DESKTOP: Cannot be pickup on area, McDia info ... etc -->
      <ProductAlerts
        :product="product"
        :not-matching-status="notMatchingStatus"
        :area="area"
        class="block md:hidden"
      />

      <PayEventInfo
        v-if="isMobile && props.product.exclusivePayEvent"
        class="my-4"
        :data="props.product.exclusivePayEvent"
        :message="messageLimitProduct"
      />

      <!-- ProductSizes -->
      <ProductSizes
        v-if="filteredProductSizes.length && showFilteredProductSizes"
        :class="{
          'pointer-events-none opacity-50 grayscale': disabledUserInteraction,
        }"
        :sizes="filteredProductSizes"
        :product-id="product.identifier"
        @change-size="(id: string) => $emit('on-change-size', id)"
      />

      <!-- Customization -->
      <Customization
        v-if="product.optionsGroups.length"
        :class="{
          'pointer-events-none opacity-50 grayscale': disabledUserInteraction,
        }"
        :options-groups="currentOptionsGroups"
        :price="computedPrice"
        @save-personalization="updatePersonalization"
        @customizing="(ev: boolean) => customizing = ev"
      >
        <template #comboActions="{ enabled, save }">
          <ProductActions
            v-model:unit="unit"
            class="relative pt-1"
            :price="computedPrice"
            :price-with-discount="cartProductPriceWithDiscount"
            :can-add="enabled"
            :editing="true"
            @save="save"
          />
        </template>
      </Customization>
    </section>
  </div>
</template>
